import type { ReactElement, Ref } from 'react';
import styles from './Switch.module.css';

interface SwitchProps {
  checked: boolean;
  /** Nome acessível: o que o interruptor liga. NÃO muda com o estado (quem diz ligado/desligado é `aria-checked`). */
  label: string;
  /**
   * Operação em andamento. O interruptor já mostra o estado novo, se anuncia como desabilitado
   * (`aria-disabled`) e ignora cliques, mas CONTINUA focável: com `disabled` de verdade o
   * navegador pode jogar o foco para o <body> no meio da operação (a mesma razão do `Button`).
   */
  busy?: boolean;
  /** Id do elemento que descreve o interruptor (a descrição acessível; o nome continua o `label`). */
  describedBy?: string;
  onToggle: () => void;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Interruptor ligar/desligar: um botão com `role="switch"` (teclado: Espaço e Enter). A área de
 * toque tem 44px; o desenho (trilho e botão redondo) é só enfeite, e a posição do botão redondo
 * diz o estado além da cor.
 */
export function Switch({
  checked,
  label,
  busy = false,
  describedBy,
  onToggle,
  ref,
}: SwitchProps): ReactElement {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      aria-disabled={busy || undefined}
      aria-busy={busy || undefined}
      className={styles.switch}
      onClick={() => {
        if (!busy) onToggle();
      }}
    >
      <span className={styles.track} aria-hidden="true">
        <span className={styles.thumb} />
      </span>
    </button>
  );
}
